import {
  Button, Column, Divider, Drawer, Heading, List, ListSubheading, Page, Row, Spacer,
} from '@oliasoft-open-source/react-ui-library';
import translations from 'client/internationalization/translation-map.json';
import { getQueryListSections } from 'client/store/entities/query/selectors';
import { OwnerType } from 'common/enums/owner-enums';
import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { ConnectedProps, connect, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { entityTableLabelMap } from 'common/table-viewer/utils';
import { RootState } from 'client/store/configureStore';
import {
  deleteQuery, getQueries, queryResultReset, upsertQuery,
} from 'client/store/entities/query/query';
import { IQuery } from 'common/interfaces/query';
import { QueryCreateModal } from './query-create-modal';
import { QueryForm } from './query-form/query-form';
import { getEntityParamLabel } from './query-utils';

const Query = ({
  queries,
  sections,
  getQueries,
  deleteQuery,
  upsertQuery,
}: PropsFromRedux) => {
  const [activeQuery, setActiveQuery] = useState<IQuery>();
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { companyId } = useParams();

  useEffect(() => {
    getQueries();
  }, []);

  useEffect(() => {
    setActiveQuery(currentQuery => queries.find(query => query.queryid === currentQuery?.queryid));
  }, [queries]);

  useEffect(() => {
    dispatch(queryResultReset());
  }, [activeQuery]);

  const onCreateQuery = (query: IQuery) => {
    upsertQuery(query);
    setActiveQuery(query);
    setExpandedSections(sections => ({ ...sections, [query.ownerType]: true }));
    setCreateModalOpen(false);
  };

  const toQueryItem = (query: IQuery) => {
    const firstCondition = query?.conditionGroups?.[0]?.conditions[0];
    return {
      name: query.queryName,
      active: query.queryid === activeQuery?.queryid,
      onClick: () => setActiveQuery(query),
      actions: [{
        type: 'Menu',
        subActions: [{
          label: t(translations.delete),
          icon: 'delete',
          onClick: () => deleteQuery(query.queryid || ''),
        },
        {
          label: t(translations.query_duplicateQuery),
          icon: 'duplicate',
          onClick: () => upsertQuery({ ...query, queryid: uuidv4(), ownerType: OwnerType.USER }),
        }],
      }],
      // TODO clarify what we should show here
      details: firstCondition ? `${entityTableLabelMap[firstCondition.entityType]} ${getEntityParamLabel(firstCondition.entityType, firstCondition.entityPropertyName)}` : '',
      metadata: query.groupBy ? `${t(translations.query_groupedBy)} ${query.groupBy}` : '',
    };
  };

  const toggleSection = (sectionKey: string) => {
    setExpandedSections(sections => ({ ...sections, [sectionKey]: !sections[sectionKey] }));
  };

  return (
    <Page padding={false} scroll={false}>
      <Row spacing={0} height="100%">
        <Drawer button={false} open background="var(--color-neutral-50)">
          <div style={{ padding: 'var(--padding)' }}>
            <Row>
              <Column>
                <Heading top marginBottom={0}>{t(translations.query)}</Heading>
              </Column>
              <Column width="var(--padding-l)">
                <Button round small colored icon="add" onClick={() => setCreateModalOpen(true)} />
              </Column>
            </Row>
            <Spacer height="var(--padding-xs)" />
          </div>
          <Divider margin={0} />
          {sections.map(({ key, nameKey, sectionQueries }) => (
            <Fragment key={key}>
              <ListSubheading
                item={{
                  name: t(nameKey),
                  metaCount: sectionQueries.length,
                  expanded: !!expandedSections[key],
                  onClick: () => toggleSection(key),
                }}
              />
              {expandedSections[key] && (
                <List noHeader list={{ items: sectionQueries.map(toQueryItem) }} />
              )}
            </Fragment>
          ))}
        </Drawer>
        <Column scroll borderLeft>
          <div style={{ padding: '20px' }}>
            {activeQuery && (
              <QueryForm
                selectedQuery={activeQuery}
                setSelectedQuery={(setActiveQuery)}
                savedQuery={queries.find(query => query.queryid === activeQuery.queryid)}
              />
            )}
          </div>
        </Column>
      </Row>
      {isCreateModalOpen && (
        <QueryCreateModal
          companyId={companyId!}
          onClose={() => setCreateModalOpen(false)}
          onSubmit={onCreateQuery}
        />
      )}
    </Page>
  );
};

const mapStateToProps = ({ entities }: RootState) => ({
  queries: entities?.query.queries,
  queryResult: entities?.query.result,
  isRunning: entities?.query.isRunning,
  sections: getQueryListSections(entities?.query),
});

const mapDispatchToProps = {
  getQueries,
  deleteQuery,
  upsertQuery,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Container = connector(Query);

export { Container as Query };
